import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardDefaultRange } from '@/hooks/api/useDashboardDefaultRange';
import { AlertsWidget } from './widgets/Alerts';
import { CoverageOverTime } from './widgets/CoverageOverTime';
import { LatestAlertsWidget } from './widgets/LatestAlertsWidget';
import { MCPServersUsageWidget } from './widgets/MCPUsage';
import { PoliciesCoverageWidget } from './widgets/PoliciesCoverage';
import { TopAlertsWidget } from './widgets/TopAlerts';
import { DashboardErrorBoundary } from './DashboardErrorBoundary';
import { RiskMapSection } from './RiskMap';
import { TopShadowAIUsers } from './widgets/TopShadowAIUsers';
import { MostActiveAITool } from './widgets/MostActiveAITool';
import { TopBrowserAITools } from './widgets/TopBrowserAITools';
import { TopAIInstalledTools } from './widgets/TopAIInstalledTools';
import {
  DashboardDefaultRangeContext,
  type DashboardDefaultRange,
} from './dashboardDefaultRange';

/** Holds a time-ranged widget's grid cell while its opening range loads. */
function RangedWidgetSkeleton({ className }: { className: string }) {
  return (
    <div
      data-testid="dashboard-range-loading"
      className={`col-span-12 ${className}`}
    >
      <Skeleton className="h-[300px] w-full rounded-xl" />
    </div>
  );
}

interface DashboardProps {
  isShadowAIEnabled?: boolean;
  /** KIR-5075: show the AI Risk Map area at the top of the dashboard. */
  isAiRiskMapEnabled?: boolean;
}

export const Dashboard = ({
  isShadowAIEnabled = false,
  isAiRiskMapEnabled = false,
}: DashboardProps) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  // KIR-5693: a very busy organization opens its time-ranged widgets on the
  // last day. Those widgets load only once that is known, so none sends a
  // 7-day request first; the others load right away. An error opens on the
  // usual 7 days.
  const defaultRangeQuery = useDashboardDefaultRange(organizationId);
  const awaitingRange = !!organizationId && defaultRangeQuery.isPending;
  const defaultRange: DashboardDefaultRange =
    defaultRangeQuery.data?.defaultRange ?? '7d';

  return (
    <DashboardErrorBoundary>
      {/* Keyed on the organization: widgets take the opening range when they
          mount, so another org's (cached) answer must remount them. */}
      <DashboardDefaultRangeContext.Provider
        key={organizationId}
        value={defaultRange}
      >
        <div className="mx-auto space-y-6">
          {defaultRange === '1d' && (
            <p
              data-testid="dashboard-busy-org-note"
              className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
            >
              Showing the last day: your organization has a lot of recent
              activity. Choose a longer time range on a widget to see more.
            </p>
          )}
          {isAiRiskMapEnabled && (
            <div className="border-b pb-6">
              {awaitingRange ? (
                <div data-testid="dashboard-range-loading">
                  <Skeleton className="h-[640px] w-full rounded-xl" />
                </div>
              ) : (
                <RiskMapSection />
              )}
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold">General Usage</h2>
            <p className="text-sm text-muted-foreground mt-1">
              A high-level view of organization-wide alerts, coverage, and MCP
              usage.
            </p>
          </div>

          <div className="space-y-6 ml-4">
            <div className="grid grid-cols-12 gap-6">
              {/* Top Alerts Widget */}
              {awaitingRange ? (
                <RangedWidgetSkeleton className="xl:col-span-4" />
              ) : (
                <TopAlertsWidget />
              )}
              {/* Alerts Chart Widget */}
              {awaitingRange ? (
                <RangedWidgetSkeleton className="xl:col-span-8" />
              ) : (
                <AlertsWidget />
              )}
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Latest Alerts Widget */}
              <LatestAlertsWidget />
              {/* Kirin Coverage Widget */}
              {awaitingRange ? (
                <RangedWidgetSkeleton className="xl:col-span-6" />
              ) : (
                <CoverageOverTime />
              )}
              {/* Policies Coverage Widget */}
            </div>

            <div className="grid grid-cols-12 gap-6">
              <PoliciesCoverageWidget />
              <MCPServersUsageWidget />
            </div>
          </div>

          {isShadowAIEnabled && (
            <div className="space-y-3 border-t pt-6 ">
              <div>
                <h2 className="text-lg font-semibold">Shadow AI Insights</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Monitor Shadow AI activity, usage patterns, and risk signals
                  for your organization.
                </p>
              </div>
              <div className="space-y-6 ml-4">
                <div className="grid grid-cols-12 gap-6">
                  <TopShadowAIUsers />
                  <TopAIInstalledTools />
                </div>
                <div className="grid grid-cols-12 gap-6">
                  <MostActiveAITool />
                  <TopBrowserAITools />
                </div>
              </div>
            </div>
          )}
        </div>
      </DashboardDefaultRangeContext.Provider>
    </DashboardErrorBoundary>
  );
};
